'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Link from '@/core/i18n/navigation';
import { useTranslation } from '@/core/hooks/useTranslation';
import { BipLink } from '@/core/components/BipLink';
import { useAutoTranslateLabels } from '@/core/hooks/useAutoTranslateLabels';
import { trackClick } from '@/core/utils/analytics';
import { telHref } from '@/core/utils/phone';
import { ResponsiveImage } from '@/core/components/Builder/shared/ResponsiveImage';
import { getAbsoluteMediaUrl } from '@/core/utils/media';

// ── Color helpers ────────────────────────────────────────────────────
// Footer colors (bg/text) are merchant-configured — we never override the
// palette itself, only make sure the "muted" secondary text we derive from
// it stays legible against the merchant's chosen background.

function hexToRgbTuple(hex: string): [number, number, number] {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    return result
        ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
        : [0, 0, 0];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
    return `#${[r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
    const [rs, gs, bs] = [r, g, b].map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function contrastRatio(hexA: string, hexB: string): number {
    const lA = relativeLuminance(hexToRgbTuple(hexA));
    const lB = relativeLuminance(hexToRgbTuple(hexB));
    const [lighter, darker] = lA >= lB ? [lA, lB] : [lB, lA];
    return (lighter + 0.05) / (darker + 0.05);
}

// Blends textColor toward bgColor; t=0 -> bgColor, t=1 -> textColor (opaque).
function blend(bgColor: string, textColor: string, t: number): string {
    const bg = hexToRgbTuple(bgColor);
    const fg = hexToRgbTuple(textColor);
    return rgbToHex(bg.map((c, i) => c + (fg[i] - c) * t) as [number, number, number]);
}

// Reproduces the previous `${textColor}99` (~60% opacity) "muted" look, but
// walks the blend toward full-strength textColor if that 60% blend would
// fail WCAG AA (4.5:1) against the actual footer background — instead of a
// fixed alpha that can land anywhere depending on the merchant's colors.
function getAccessibleMutedColor(textColor?: string, bgColor?: string): string | undefined {
    if (!textColor) return undefined;
    const bg = bgColor || '#ffffff';
    for (let t = 0.6; t <= 1; t += 0.05) {
        const candidate = blend(bg, textColor, t);
        if (contrastRatio(candidate, bg) >= 4.5) return candidate;
    }
    return textColor;
}

// ── Types ─────────────────────────────────────────────────────────────

interface FooterLinkItem { label: string; url: string; }
interface SocialLinkItem { platform: string; url: string; }

export type FooterBlockType = 'links' | 'contact' | 'image' | 'address' | 'text' | 'social';

export interface FooterBlock {
    id: string;
    type: FooterBlockType;
    title?: string;
    // links
    links?: FooterLinkItem[];
    // contact
    email?: string;
    phone?: string;
    // image
    image_url?: string;
    image_alt?: string;
    image_width?: number;
    // address
    address?: string;
    // text
    text?: string;
    // social
    social_links?: SocialLinkItem[];
}

export interface FooterColumnConfig {
    id: string;
    padding_top: number;
    padding_right: number;
    padding_bottom: number;
    padding_left: number;
    blocks: FooterBlock[];
}

export interface FooterBar {
    copyright_text?: string;
    bg_color?: string;
    text_color?: string;
    padding_y?: number;
    alignment?: 'left' | 'center' | 'right';
}

export interface FooterConfig {
    bg_color?: string;
    text_color?: string;
    border_style?: 'none' | 'top' | 'all';
    border_color?: string;
    border_width?: number;
    border_type?: 'solid' | 'dashed' | 'dotted';
    border_shadow?: boolean;
    border_shadow_color?: string;
    border_shadow_size?: number;
    padding_top?: number;
    padding_bottom?: number;
    columns_gap?: number;
    title_font_size?: number;
    link_font_size?: number;
    columns?: FooterColumnConfig[];
    bar?: FooterBar;
    // Typography
    font_family?: string;
    font_weight?: string;
    letter_spacing?: string;
    // Legacy fields kept for backwards compatibility
    copyright_text?: string;
    description?: string;
    content_columns?: { title: string; links: FooterLinkItem[] }[];
    social_links?: SocialLinkItem[];
    contact_email?: string;
    contact_phone?: string;
    contact_address?: string;
    show_divider?: boolean;
}

interface FooterProps {
    siteName: string;
    footerConfig?: FooterConfig | null;
}

const SOCIAL_ICONS: Record<string, string> = {
    facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z',
    instagram: 'M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37zM17.5 6.5h.01M7.5 3.5h9a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9a4 4 0 0 1-4-4v-9a4 4 0 0 1 4-4z',
    youtube: 'M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z',
    tiktok: 'M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.24 8.24 0 0 0 4.83 1.55V6.79a4.85 4.85 0 0 1-1.06-.1z',
    linkedin: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    x: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
};

function SocialIcon({ platform }: { platform: string }) {
    const d = SOCIAL_ICONS[platform.toLowerCase()];
    if (!d) return null;
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d={d} />
        </svg>
    );
}

/** Links column. On phones a titled list with several links collapses behind its title (a footer with
 * four open columns was several screens long); from md up it is always open, as before. */
function LinksBlock({ block, titleStyle, linkStyle, tr }: {
    block: FooterBlock;
    titleStyle: React.CSSProperties;
    linkStyle: React.CSSProperties;
    tr: (text: string | undefined) => string | undefined;
}) {
    const [open, setOpen] = useState(false);
    const links = block.links || [];
    const collapsible = !!block.title && links.length > 3;
    return (
        <div>
            {block.title && (collapsible ? (
                <>
                    <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
                        className="md:hidden w-full flex items-center justify-between font-semibold py-1" style={titleStyle}>
                        <span>{tr(block.title)}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    <h3 className="hidden md:block font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>
                </>
            ) : (
                <h3 className="font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>
            ))}
            {links.length > 0 && (
                <ul className={`space-y-2 ${collapsible ? (open ? 'mt-3 md:mt-0' : 'hidden md:block') : ''}`}>
                    {links.map((link, i) => (
                        <li key={i}>
                            <Link href={link.url || '#'} className="hover:opacity-80 transition-opacity" style={linkStyle}>
                                {tr(link.label)}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function BlockRenderer({
    block,
    textColor,
    mutedColor,
    titleFontSize,
    linkFontSize,
    tr,
}: {
    block: FooterBlock;
    textColor?: string;
    mutedColor?: string;
    titleFontSize: number;
    linkFontSize: number;
    // Looks up the auto-translated version of admin-authored free text (block titles, link
    // labels, text/address content) — falls back to the original when no translation is cached
    // yet (locale === 'pl', or the async /translate-batch call hasn't resolved).
    tr: (text: string | undefined) => string | undefined;
}) {
    const titleStyle = { color: textColor, fontSize: `${titleFontSize}px` };
    const linkStyle = { color: mutedColor, fontSize: `${linkFontSize}px` };

    switch (block.type) {
        case 'links':
            return <LinksBlock block={block} titleStyle={titleStyle} linkStyle={linkStyle} tr={tr} />;
        case 'contact':
            return (
                <div>
                    {block.title && <h3 className="font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>}
                    <ul className="space-y-2">
                        {block.email && (
                            <li><a href={`mailto:${block.email}`} onClick={() => trackClick('mailto', block.email || '')} className="hover:opacity-80 transition-opacity" style={linkStyle}>{block.email}</a></li>
                        )}
                        {block.phone && (
                            <li><a href={telHref(block.phone)} onClick={() => trackClick('tel', block.phone || '')} className="hover:opacity-80 transition-opacity" style={linkStyle}>{block.phone}</a></li>
                        )}
                    </ul>
                </div>
            );
        case 'image':
            if (!block.image_url) return null;
            return (
                <div>
                    {block.title && <h3 className="font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>}
                    {/* With a set width, through the optimizer like every other storefront image: the raw
                        upload went out as-is — a 60 KB webp, from the backend's /static instead of the
                        CDN, for a logo shown ~160px wide. Without one the rendered size is the file's own,
                        which a srcset would change, so it stays a plain <img> (CDN URL at least).
                        Footer = below the fold: lazy, or React's SSR also emits a preload for it,
                        competing with the CSS on the critical path. */}
                    {block.image_width ? (
                        <ResponsiveImage
                            src={block.image_url}
                            alt={block.image_alt || ''}
                            sizes={`${block.image_width}px`}
                            style={{ maxWidth: `${block.image_width}px`, height: 'auto' }}
                        />
                    ) : (
                        <img
                            src={getAbsoluteMediaUrl(block.image_url.startsWith('/') || /^(https?|data|blob):/.test(block.image_url) ? block.image_url : `/${block.image_url}`)}
                            alt={block.image_alt || ''}
                            loading="lazy"
                            decoding="async"
                            style={{ maxWidth: '100%', height: 'auto' }}
                        />
                    )}
                </div>
            );
        case 'address':
            return (
                <div>
                    {block.title && <h3 className="font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>}
                    {block.address && (
                        <p className="whitespace-pre-line" style={linkStyle}>{tr(block.address)}</p>
                    )}
                </div>
            );
        case 'text':
            return (
                <div>
                    {block.title && <h3 className="font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>}
                    {block.text && (
                        <p className="leading-relaxed" style={linkStyle}>{tr(block.text)}</p>
                    )}
                </div>
            );
        case 'social':
            if (!block.social_links?.length) return null;
            return (
                <div>
                    {block.title && <h3 className="font-semibold mb-3" style={titleStyle}>{tr(block.title)}</h3>}
                    <div className="flex flex-wrap gap-3">
                        {block.social_links.map((s, i) => (
                            <Link key={i} href={s.url || '#'} target="_blank" rel="noopener noreferrer" title={s.platform}
                                className="w-8 h-8 rounded-full border flex items-center justify-center hover:opacity-80 transition-opacity"
                                style={{ borderColor: mutedColor ? `${mutedColor}44` : '#e5e7eb', color: mutedColor }}>
                                <SocialIcon platform={s.platform} />
                            </Link>
                        ))}
                    </div>
                </div>
            );
        default:
            return null;
    }
}

export function Footer({ siteName, footerConfig }: FooterProps) {
    const { t, locale } = useTranslation();
    const cfg = footerConfig ?? {};
    const bgColor = cfg.bg_color;
    const textColor = cfg.text_color;
    const borderStyle = cfg.border_style ?? 'top';
    const separatorColor = cfg.border_color || 'rgba(0,0,0,0.08)';
    const separatorWidth = cfg.border_width ?? 1;
    const separatorType = cfg.border_type || 'solid';
    const hasShadow = cfg.border_shadow ?? false;
    const shadowColor = cfg.border_shadow_color || 'rgba(0,0,0,0.06)';
    const shadowSize = cfg.border_shadow_size ?? 8;
    const paddingTop = cfg.padding_top ?? 40;
    const paddingBottom = cfg.padding_bottom ?? 24;
    const columnsGap = cfg.columns_gap ?? 32;
    const titleFontSize = cfg.title_font_size ?? 14;
    const linkFontSize = cfg.link_font_size ?? 14;

    const borderClass = '';
    const footerBorderStyle: React.CSSProperties = borderStyle !== 'none' && separatorWidth > 0 ? (
        borderStyle === 'all'
            ? { border: `${separatorWidth}px ${separatorType} ${separatorColor}` }
            : { borderTop: `${separatorWidth}px ${separatorType} ${separatorColor}` }
    ) : {};
    const footerShadow: React.CSSProperties = hasShadow
        ? { boxShadow: `0 -${shadowSize}px ${shadowSize * 2}px 0 ${shadowColor}` }
        : {};
    const mutedColor = getAccessibleMutedColor(textColor, bgColor);
    const dividerColor = textColor ? `${textColor}22` : undefined;

    const year = new Date().getFullYear();

    // New column-based layout
    // Imported/hand-edited configs can carry columns without blocks — never let that crash every page.
    const columns = Array.isArray(cfg.columns)
        ? cfg.columns.map(col => ({ ...col, blocks: Array.isArray(col?.blocks) ? col.blocks : [] }))
        : cfg.columns;
    const bar = cfg.bar;

    // Custom copyright text (admin-typed in Wygląd, e.g. "© {year} {site_name}. Wszelkie prawa
    // zastrzeżone.") — kept separate from the auto-generated default, which already goes through
    // t("footer.copyright_suffix", ...) and doesn't need live translation.
    const customCopyright = bar?.copyright_text ?? cfg.copyright_text ?? null;
    // {year}/{site_name} are substituted BEFORE translation, not after: machine translation
    // (LibreTranslate/Google) does not reliably leave brace-wrapped tokens untouched — in Dutch it
    // translated the word "year" inside the braces too, turning "{year}" into "{jaar}", which the
    // later literal .replace('{year}', ...) could no longer match.
    const resolvedCopyrightTemplate = customCopyright
        ? customCopyright.replace('{year}', String(year)).replace('{site_name}', siteName || 'ShopStag')
        : null;

    // Footer content (column block titles/links/text/address, legacy content_columns,
    // description, custom copyright) is admin-authored free text with no per-locale field — same
    // problem as header nav links, so it goes through the same auto-translate mechanism.
    const footerLabels: string[] = [
        ...(columns ?? []).flatMap(col => col.blocks.flatMap(b => [
            b.title,
            ...(b.links?.map(l => l.label) ?? []),
            b.text,
            b.address,
        ])),
        cfg.description,
        ...(cfg.content_columns ?? []).flatMap(col => [col.title, ...col.links.map(l => l.label)]),
        resolvedCopyrightTemplate,
    ].filter((s): s is string => !!s);
    const translatedFooterLabels = useAutoTranslateLabels(footerLabels, locale);
    const tr = (text: string | undefined): string | undefined => text ? (translatedFooterLabels[text] || text) : text;

    // Legacy fallback
    const hasLegacy = !columns && (
        (cfg.content_columns && cfg.content_columns.length > 0) ||
        cfg.description ||
        cfg.contact_email ||
        cfg.contact_phone ||
        (cfg.social_links && cfg.social_links.length > 0)
    );

    const defaultCopyright = `© ${year} ${siteName || 'ShopStag'}. ${t("footer.copyright_suffix", "Wszelkie prawa zastrzeżone.")}`;
    const barText = resolvedCopyrightTemplate
        ? (tr(resolvedCopyrightTemplate) || resolvedCopyrightTemplate)
        : defaultCopyright;
    const barBg = bar?.bg_color;
    const barTextColor = bar?.text_color ?? mutedColor;
    const barPaddingY = bar?.padding_y ?? 16;
    const barAlignment = bar?.alignment ?? 'left';

    const alignClass = barAlignment === 'center' ? 'justify-center text-center' : barAlignment === 'right' ? 'justify-end text-right' : 'justify-start text-left';

    return (
        <footer
            className={borderClass}
            style={{
                backgroundColor: bgColor || undefined,
                color: textColor || undefined,
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
                fontFamily: cfg.font_family || undefined,
                fontWeight: cfg.font_weight || undefined,
                letterSpacing: cfg.letter_spacing || undefined,
                ...footerBorderStyle,
                ...footerShadow,
            }}
        >
            {/* New column layout */}
            {columns && columns.length > 0 && (
                <div className="w-full px-[5%] md:px-[10%] mb-6">
                    <div className="flex flex-wrap" style={{ gap: `${columnsGap}px` }}>
                        {columns.map((col, ci) => (
                            col.blocks.length > 0 && (
                                <div
                                    key={col.id ?? ci}
                                    className="flex-1 min-w-[160px] space-y-6"
                                    style={{
                                        paddingTop: `${col.padding_top ?? 0}px`,
                                        paddingRight: `${col.padding_right ?? 0}px`,
                                        paddingBottom: `${col.padding_bottom ?? 0}px`,
                                        paddingLeft: `${col.padding_left ?? 0}px`,
                                    }}
                                >
                                    {col.blocks.map((block, bi) => (
                                        <BlockRenderer
                                            key={block.id ?? bi}
                                            block={block}
                                            textColor={textColor}
                                            mutedColor={mutedColor}
                                            titleFontSize={titleFontSize}
                                            linkFontSize={linkFontSize}
                                            tr={tr}
                                        />
                                    ))}
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}

            {/* Legacy layout */}
            {hasLegacy && !columns && (
                <div className="w-full px-[5%] md:px-[10%] mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                        {(cfg.description || (cfg.social_links && cfg.social_links.length > 0)) && (
                            <div className="md:col-span-3 space-y-4">
                                {cfg.description && (
                                    <p className="text-sm leading-relaxed" style={{ color: mutedColor }}>{tr(cfg.description)}</p>
                                )}
                                {cfg.social_links && cfg.social_links.length > 0 && (
                                    <div className="flex flex-wrap gap-3">
                                        {cfg.social_links.map((s, i) => (
                                            <Link key={i} href={s.url || '#'} target="_blank" rel="noopener noreferrer" title={s.platform}
                                                className="w-8 h-8 rounded-full border flex items-center justify-center hover:opacity-80 transition-opacity"
                                                style={{ borderColor: dividerColor, color: mutedColor }}>
                                                <SocialIcon platform={s.platform} />
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {cfg.content_columns?.map((col, i) => (
                            <div key={i} className="md:col-span-2">
                                <h3 className="font-semibold text-sm mb-3" style={{ color: textColor || undefined }}>{tr(col.title)}</h3>
                                <ul className="space-y-2">
                                    {col.links.map((link, j) => (
                                        <li key={j}>
                                            <Link href={link.url || '#'} className="text-sm hover:opacity-80 transition-opacity" style={{ color: mutedColor }}>
                                                {tr(link.label)}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        {(cfg.contact_email || cfg.contact_phone || cfg.contact_address) && (
                            <div className="md:col-span-3">
                                <h3 className="font-semibold text-sm mb-3" style={{ color: textColor || undefined }}>{t("footer.contact", "Kontakt")}</h3>
                                <ul className="space-y-2 text-sm" style={{ color: mutedColor }}>
                                    {cfg.contact_email && <li><a href={`mailto:${cfg.contact_email}`} onClick={() => trackClick('mailto', cfg.contact_email || '')} className="hover:opacity-80">{cfg.contact_email}</a></li>}
                                    {cfg.contact_phone && <li><a href={telHref(cfg.contact_phone)} onClick={() => trackClick('tel', cfg.contact_phone || '')} className="hover:opacity-80">{cfg.contact_phone}</a></li>}
                                    {cfg.contact_address && <li className="whitespace-pre-line">{cfg.contact_address}</li>}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bottom bar */}
            <div
                style={{
                    backgroundColor: barBg || undefined,
                    borderTopColor: dividerColor,
                }}
                className={`${(columns || hasLegacy) ? 'border-t' : ''}`}
            >
                <div
                    className={`w-full px-[5%] md:px-[10%] flex flex-wrap items-center gap-4 ${alignClass}`}
                    style={{ paddingTop: `${barPaddingY}px`, paddingBottom: `${barPaddingY}px` }}
                >
                    <p className="text-sm" style={{ color: barTextColor }}>{barText}</p>
                    <BipLink color={barTextColor} />
                </div>
            </div>
        </footer>
    );
}
